// Deals: the board, closed deals, a deal's detail and the deals shown on a record, plus the pure rules the
// server and the browser share (card positions, the stale warning, next-step due states).

import { addDays, daysBetween, localDayKey, startOfDay } from './home';
import type { CompanyRef, Ref, Stage, StageColor } from './types';

/* ---------- shapes ---------- */

/** A deal as it shows on the board and in lists. Money is in the deal's own currency plus the main currency. */
export interface DealCard {
  id: string;
  name: string;
  stageId: string;
  position: number;
  value: number;
  currency: string;
  /** The value in the workspace's main currency. */
  valueMain: number;
  company: CompanyRef | null;
  person: Ref | null;
  owner: Ref | null;
  nextStep: string | null;
  nextStepAt: string | null;
  nextStepHasTime: boolean;
  lastActivityAt: string;
  stageChangedAt: string;
  tags: string[];
}

export interface BoardColumn {
  stageId: string;
  /** Every deal in the stage that matches the filters, even when only the first ones are listed. */
  count: number;
  total: number;
  deals: DealCard[];
}

export interface QuarterSummary {
  count: number;
  total: number;
  /** Start of the current quarter (ISO). */
  since: string;
}

export interface BoardResponse {
  mainCurrency: string;
  staleAfterDays: number;
  /** Every stage in order, the closed ones included. */
  stages: Stage[];
  /** One column per open stage, in stage order. */
  columns: BoardColumn[];
  /** Open deals matching the filters. */
  count: number;
  total: number;
  /** Deals per stage across the whole team, open and closed, ignoring filters and the bin (for the stages editor). */
  stageCounts: Record<string, number>;
  /** Tags used on open deals, for the tag filter. */
  tags: string[];
  won: QuarterSummary;
  lost: QuarterSummary;
  /** How many cards each column lists before "Show all". */
  limit: number;
}

export type DealStatus = 'open' | 'won' | 'lost';

export interface ClosedDeal extends DealCard {
  status: 'won' | 'lost';
  closedAt: string;
  closedBy: Ref | null;
  closeReason: string | null;
  closeNote: string | null;
  /** The open stage the deal was in before it closed, when it is still on the board. */
  previousStageId: string | null;
  /** Until when you can undo the close (whoever closed a deal can, for 15 minutes), or null. */
  undoCloseUntil: string | null;
}

export interface ClosedPage {
  items: ClosedDeal[];
  total: number;
  nextCursor: string | null;
  quarter: QuarterSummary;
}

export interface DealDetail extends DealCard {
  status: DealStatus;
  stage: { id: string; name: string; color: StageColor; kind: Stage['kind'] };
  closedAt: string | null;
  closedBy: Ref | null;
  closeReason: string | null;
  closeNote: string | null;
  previousStageId: string | null;
  /** Until when you can undo the close (whoever closed a deal can, for 15 minutes), or null. */
  undoCloseUntil: string | null;
  custom: Record<string, unknown>;
  createdAt: string;
  createdBy: Ref | null;
  updatedAt: string;
}

/** A deal in a record's Deals box: on the person themselves, or on their company. */
export interface RecordDeal extends DealDetail {
  via: 'person' | 'company';
}

export interface RecordDealsResponse {
  mainCurrency: string;
  staleAfterDays: number;
  deals: RecordDeal[];
  openCount: number;
  openTotal: number;
}

/** Names for the new-deal dialog when it starts from a record. */
export interface DealLookup {
  company: CompanyRef | null;
  person: (Ref & { companyId: string | null }) | null;
}

export interface PickerOption {
  id: string;
  name: string;
  hue: number;
  /** For companies: the logo from their website, as a logo id. */
  logo?: string | null;
  sub: string | null;
  companyId?: string | null;
  companyName?: string | null;
  companyHue?: number | null;
  companyLogo?: string | null;
}

/** Where a deal sat before a move. An undo sends it back as `restore`. */
export interface PreviousPlace {
  stageId: string;
  position: number;
  stageChangedAt: string;
}

export interface MoveResult {
  deal: DealCard;
  from: { id: string; name: string };
  to: { id: string; name: string };
  previous: PreviousPlace;
}

/* ---------- card positions ---------- */

/** Space between neighbouring cards when a column is numbered afresh. */
export const POSITION_GAP = 1000;
/** Below this gap two neighbours are too close to fit another card between them. */
export const MIN_POSITION_GAP = 1e-6;

/**
 * The position for a card placed between two neighbours (null where there is none). Returns null when the
 * neighbours sit too close together, which means the column must be numbered afresh first.
 */
export function positionBetween(prev: number | null, next: number | null): number | null {
  if (prev === null && next === null) return POSITION_GAP;
  if (prev === null) return next! - POSITION_GAP;
  if (next === null) return prev + POSITION_GAP;
  if (!(next - prev >= MIN_POSITION_GAP * 2)) return null;
  const mid = prev + (next - prev) / 2;
  return mid > prev && mid < next ? mid : null;
}

export interface Positioned {
  id: string;
  position: number;
}

/** Where a moved card goes: after a card, before a card, or (with neither) at the top of the column. */
export interface MoveAnchor {
  afterId?: string | null;
  beforeId?: string | null;
  /** With no anchor: "top" (the default) or "bottom". */
  end?: 'top' | 'bottom';
}

export interface MovePlan {
  position: number;
  /** Set when the column had to be numbered afresh: every card's new position, the moved one included. */
  renumber: Positioned[] | null;
}

/**
 * Works out the moved card's position in a column. `siblings` are the column's other cards (any order; the moved
 * card is ignored if present). An anchor that is no longer in the column falls back to the column's end.
 */
export function planMove(siblings: Positioned[], movingId: string, anchor: MoveAnchor): MovePlan {
  const list = siblings.filter((s) => s.id !== movingId).sort((a, b) => a.position - b.position);
  let index: number;
  const after = anchor.afterId ? list.findIndex((s) => s.id === anchor.afterId) : -1;
  const before = anchor.beforeId ? list.findIndex((s) => s.id === anchor.beforeId) : -1;
  if (after >= 0) index = after + 1;
  else if (before >= 0) index = before;
  else if (anchor.afterId) index = list.length;
  else if (anchor.beforeId) index = 0;
  else index = anchor.end === 'bottom' ? list.length : 0;
  const prev = list[index - 1]?.position ?? null;
  const next = list[index]?.position ?? null;
  const position = positionBetween(prev, next);
  if (position !== null) return { position, renumber: null };
  const ordered = [...list.slice(0, index).map((s) => s.id), movingId, ...list.slice(index).map((s) => s.id)];
  const renumber = ordered.map((id, i) => ({ id, position: (i + 1) * POSITION_GAP }));
  return { position: renumber[index]!.position, renumber };
}

/** Moves an id to a new index in a list of ids (for optimistic updates). */
export function moveInList(ids: string[], id: string, index: number): string[] {
  const rest = ids.filter((x) => x !== id);
  const at = Math.max(0, Math.min(rest.length, index));
  return [...rest.slice(0, at), id, ...rest.slice(at)];
}

/* ---------- days, staleness, next steps ---------- */

const DAY_MS = 86_400_000;

function localDay(d: Date): number {
  return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Whole calendar days from `from` to `now`: 0 the same day, 1 the day before, negative in the future. Days follow
 * the time zone `tz` when given (the server passes the viewer's), otherwise the local one (the browser's).
 */
export function daysSince(from: Date | string, now: Date = new Date(), tz?: string): number {
  const d = from instanceof Date ? from : new Date(from);
  if (tz) return daysBetween(localDayKey(d, tz), localDayKey(now, tz));
  return Math.round((localDay(now) - localDay(d)) / DAY_MS);
}

/**
 * Days without activity when an open deal counts as stale, otherwise null. A deal turns stale once more than
 * `staleAfterDays` whole calendar days have passed since its last activity.
 */
export function staleDays(lastActivityAt: Date | string | null, staleAfterDays: number, now: Date = new Date(), tz?: string): number | null {
  if (!lastActivityAt) return null;
  const days = daysSince(lastActivityAt, now, tz);
  return days > staleAfterDays ? days : null;
}

/**
 * The same rule as a moment, for queries: activity before the start of the day `staleAfterDays` days before today
 * in `tz` is stale.
 */
export function staleCutoff(staleAfterDays: number, now: Date, tz: string): Date {
  return startOfDay(addDays(localDayKey(now, tz), -staleAfterDays), tz);
}

export type NextStepState = 'overdue' | 'today' | 'upcoming';

/** Whether a next step is overdue, due today or still to come, by calendar day. */
export function nextStepState(at: Date | string | null, now: Date = new Date()): NextStepState | null {
  if (!at) return null;
  const days = daysSince(at, now);
  if (days > 0) return 'overdue';
  if (days === 0) return 'today';
  return 'upcoming';
}

/** Start of the calendar quarter a moment falls in, in the given time zone (Home uses the same boundary). */
export function quarterStart(now: Date = new Date(), tz = 'UTC'): Date {
  const today = localDayKey(now, tz);
  const month = Number(today.slice(5, 7));
  return startOfDay(`${today.slice(0, 5)}${String(Math.floor((month - 1) / 3) * 3 + 1).padStart(2, '0')}-01`, tz);
}

/** Stale-rule choices offered in the stages editor. */
export const STALE_CHOICES = [7, 14, 21, 30] as const;
